import { useEffect, useState } from 'react';
import { useTheme, useMediaQuery } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { REQUEST_ACTIONS } from '~/Constants/Actions.constants';
import { SortByNameAscending, SortByNameDescending } from '~/Helpers/Sorting.helpers';
import { getFirstUsableProduct } from '~/Helpers/Utils.helpers';
import { ToastVariant, useDateFns, useLanguage, useNavigation, useToastMessage } from '~/Hooks';
import { useModalHelper } from '~/Hooks/useModal';
import { BookingStatus, IBookingSimple, PresenceType } from '~/Models/DTO/Booking.models';
import { ICustomerSimple } from '~/Models/DTO/Customer.model';
import { SearchCustomer } from '~/Models/DTO/SearchCustomer.model';
import {
  NeoState,
  requestCreateSingleBooking,
  requestCustomerList,
  requestEventProductsForCustomer,
} from '~/store';
import { requestClearError, selectLoading } from '~/store/app';
import {
  requestClearSelectedCustomers,
  requestCustomer,
  selectBookingsList,
  selectSelecteCustomer,
} from '~/store/bookings';
import { selectCustomerList } from '~/store/customers';
import { selectEventById } from '~/store/events';
import { requestClearBookableCustomers, selectBookableCustomerSearch } from '~/store/search';
import { DifferenceFormat } from '~/Models/DTO/Time.model';

export type Customer = ICustomerSimple | SearchCustomer;

export const getCustomerToSelect = (
  customerList: ICustomerSimple[],
  searchResults: SearchCustomer[],
) => (customerId: string | number) => {
  const isInCustomerList = customerList.find((customer) => customer.id === customerId);
  if (isInCustomerList) {
    return isInCustomerList;
  }

  return searchResults.find((customer) => customer.id === customerId);
};

export const hasActiveBooking = (
  customer: SearchCustomer | Partial<Customer>,
  bookingList: IBookingSimple[],
) => {
  const customerId = parseInt(`${customer.id}`, 10);
  const booking = bookingList.filter(
    (booking) => booking.customer.id === customerId && booking.status === BookingStatus.BOOKED,
  );
  if (booking.length) {
    return true;
  }
  return false;
};

export const sortCustomersBy = (customers: Partial<Customer>[], sortBy: string) => {
  switch (sortBy) {
    case 'nameasc':
      return customers.sort((a, b) => SortByNameAscending(a.lastname ?? '', b.lastname ?? ''));
    case 'namedesc':
      return customers.sort((a, b) => SortByNameDescending(a.lastname ?? '', b.lastname ?? ''));
    default:
      return customers.sort((a, b) => SortByNameAscending(a.lastname ?? '', b.lastname ?? ''));
  }
};

export const useBookingDialogHook = () => {
  const [sortedBy, setSortedBy] = useState('nameasc');
  const [isAllbookingSuccess, setSuccess] = useState(false);
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const createBooking = bindActionCreators(requestCreateSingleBooking, dispatch);
  const loadCustomerList = bindActionCreators(requestCustomerList, dispatch);
  const updateSelectedCustomers = bindActionCreators(requestCustomer, dispatch);
  const clearSearch = bindActionCreators(requestClearBookableCustomers, dispatch);
  const clearSelected = bindActionCreators(requestClearSelectedCustomers, dispatch);
  const clearErrorMessage = bindActionCreators(requestClearError, dispatch);
  const getCustomerProducts = bindActionCreators(requestEventProductsForCustomer, dispatch);

  const customerResult = useSelector(selectBookableCustomerSearch);
  const customerList = useSelector(selectCustomerList);
  const bookingCreating = useSelector(selectLoading(REQUEST_ACTIONS.BOOKING_CREATE_SINGLE));

  const selectedCustomers = useSelector(selectSelecteCustomer);
  const bookingError = useSelector(
    (state: NeoState) => state.app.errors[REQUEST_ACTIONS.BOOKING_CREATE_SINGLE],
  );
  const bookingList = useSelector(selectBookingsList);
  const { showToast } = useToastMessage((toastId: string) => clearErrorMessage(toastId));

  const { t } = useLanguage();
  const { getParams, goBack } = useNavigation();
  const { differenceBetween } = useDateFns();
  const { id, domain } = getParams<{
    domain: string;
    id: string;
  }>();

  const {
    isOpen: showCustomerCreationModal,
    open: openCustomerCreationModal,
    close: closeCustomerCreationModal,
    setOpen: setShowCustomerCreationModal,
  } = useModalHelper(false);

  const event = useSelector(selectEventById(id || ''));
  const eventName = event?.name || t('app.back');

  useEffect(() => {
    if (domain) {
      loadCustomerList({ domain });
    }

    return () => {
      clearSearch();
      clearSelected();
    };
  }, [domain]);

  useEffect(() => {
    if (bookingError) {
      // Special case for failed bookings
      // The error message is non conformity hence this hack is needed =.='
      if ((bookingError?.errors?.[0] as any)?.bookingId) {
        const bookingId = (bookingError?.errors?.[0] as any).bookingId;
        const doubleBooking = bookingList.find((booking) => booking.id === bookingId);
        const customerName = `${doubleBooking?.customer.firstname ?? ''} ${
          doubleBooking?.customer.lastname ?? ''
        }`;
        showToast(
          REQUEST_ACTIONS.BOOKING_CREATE_SINGLE,
          // TODO clean this up, the FE shouldn't throw BE errors
          t('fcs.customerIsAlreadyBooked', { customer: customerName }),
          ToastVariant.ERROR,
        );
      } else {
        showToast('BookingDialogError', t('app.default.error'), ToastVariant.ERROR);
      }
    }
  }, [bookingError, bookingList]);

  const handleSubmitBookings = async () => {
    setSuccess(false);
    const results = [];
    const isEventIn30Mins =
      differenceBetween(new Date().toString(), event.start, DifferenceFormat.MINUTES) >= -30;

    for (let i = 0, l = selectedCustomers.length; i < l; i++) {
      const nextCustomer = selectedCustomers[i];
      let orderlineToUse;
      try {
        // eslint-disable-next-line no-await-in-loop
        const availProducts = await getCustomerProducts(`${nextCustomer.id}`, event.id);
        orderlineToUse = getFirstUsableProduct(availProducts as any);
      } catch (e) {
        console.error(e);
      }

      results.push(
        createBooking({
          customerId: nextCustomer.id as number,
          eventId: event.id,
          force: true,
          presenceType: isEventIn30Mins ? PresenceType.PRESENT : PresenceType.NONE,
          orderlineId: orderlineToUse?.id ?? null,
        }),
      );
    }
    try {
      await Promise.all(results);
      setSuccess(true);
    } catch (e) {
      console.error(e);
    }

    if (isMobile) {
      goBack();
    }
  };

  const handleSortSelect = (sortBy: string) => {
    setSortedBy(sortBy);
  };

  const sortedCustomerList = sortCustomersBy(customerList, sortedBy);

  const handleCustomerSelect = (customerId: string | number) => {
    const getCustomer = getCustomerToSelect(customerList, customerResult);
    const customerToSelect = getCustomer(customerId);
    if (customerToSelect) {
      updateSelectedCustomers(customerToSelect);
    }
  };

  const filteredSearch = customerResult.filter(
    (customer) => !hasActiveBooking(customer, bookingList),
  );

  const filteredCustomerList = sortedCustomerList.filter(
    (customer) => !hasActiveBooking(customer, bookingList),
  );

  return {
    customerResult: filteredSearch,
    customers: filteredCustomerList,
    searchInput: '',
    selectedCustomers,
    handleSubmitBookings,
    handleCustomerSelect,
    bookingCreating,
    bookingList,
    handleSortSelect,
    sortedBy,
    setShowCustomerCreationModal,
    openCustomerCreationModal,
    closeCustomerCreationModal,
    showCustomerCreationModal,
    getCustomerToSelect,
    eventName,
    isAllbookingSuccess,
  };
};
